import Router from 'next/router'
import * as React from 'react'
import styled from 'styled-components'
import { Instagram } from 'styled-icons/boxicons-logos/Instagram'
import { MapPin } from 'styled-icons/boxicons-regular/MapPin'
import { Phone } from 'styled-icons/boxicons-solid/Phone'
import { Place, Web, Payment, Train } from 'styled-icons/material/'
import { Avatar } from '../frontend-lib/components/common/Avatar'
import { Button } from '../frontend-lib/components/common/Button'
import Container from '../frontend-lib/components/common/Container'
import Slider from '../frontend-lib/components/common/Slider/Slider'
import { GoogleMap } from '../frontend-lib/components/common/GoogleMap'
import Heading from '../frontend-lib/components/common/Heading'
import Section from '../frontend-lib/components/common/Section'
import { MultiLineText } from '../frontend-lib/components/MultiLineText'
import { GlobalContext } from '../frontend-lib/globalContext'
import { getPreferredLanguage, loadCatalogue } from '../frontend-lib/i18n'
import { I18nCatalogue } from '../frontend-lib/i18n/stylists/types'
import { routeFactories } from '../frontend-lib/routes'
import { colors, devices } from '../frontend-lib/styleConstants'
import { fetchHairStylist, HairStylist, StyleImage } from '../frontend-lib/types/hairStylist'
import { addStylistToFavorite, removeFavorite } from '../frontend-lib/types/me'
import { fetchMenus, Menu } from '../frontend-lib/types/menu'
import { getFavoriteByStylistId } from '../frontend-lib/types/user'
import { GeoLocation, getGeoLocationFromAddress } from '../frontend-lib/utils/google'
import { numberToCommaSeparatedString } from '../frontend-lib/utils/text'
import { KamismaPageContext } from './_app'
import ErrorComponent from './_error'
import { fetchReservedSpots, ReservedSpots } from '../frontend-lib/types/reservation'

interface Props {
  readonly catalogue: I18nCatalogue
  readonly stylist: HairStylist
  readonly menus: Menu[]
  readonly reservedSpots: ReservedSpots[]
  readonly styleImage: StyleImage
  readonly geos: GeoLocation[]
  readonly statusCode?: number
}

const Stylists = ({ catalogue, stylist, menus, reservedSpots, geos, statusCode }: Props) => {
  if (statusCode) {
    return <ErrorComponent statusCode={statusCode}></ErrorComponent>
  }

  const { me, updateContext } = React.useContext(GlobalContext)

  const favorite = React.useMemo(() => (me ? getFavoriteByStylistId(me.user, stylist.id) : null), [me, stylist.id])

  const language = getPreferredLanguage()

  const onFavoriteButtonClicked = React.useCallback(async () => {
    if (!me) {
      Router.push(routeFactories.signIn.getRoute().as!)
      return
    }

    try {
      const newMe = favorite ? await removeFavorite(favorite.id) : await addStylistToFavorite(stylist.id)
      updateContext(draft => {
        draft.me = newMe
      })
    } catch (e) {
      alert(favorite ? catalogue.removeFavoriteFailed : catalogue.addFavoriteFailed)
    }
  }, [me, favorite, stylist.id, updateContext])

  const onSignUpButtonClicked = () => {
    Router.push(routeFactories.signUp.getRoute().as!)
    return
  }

  return (
    <>
      {stylist.styleImages.length != 0 ? (
        <GallerySlide>
          <Slider
            images={stylist.styleImages.map(styleImage => {
              return styleImage.image
            })}
            isShowSwitchButton={true}
            duration={5000}
          />
        </GallerySlide>
      ) : (
        <ProfileImg src={stylist.image} />
      )}
      <Section>
        <Container>
          <Heading subHeading={stylist.job_title}>
            {stylist.name} {language != 'en' && stylist.name_en !== '' && <NameEn>{stylist.name_en}</NameEn>}
          </Heading>
          <StylistContainer>
            <StylistWrap>
              <Thumb>
                <Avatar src={stylist.image} alt={stylist.name} />
              </Thumb>
              {stylist.instagram_account && (
                <InstagramAccount>
                  <Instagram size={25} />
                  <a href={`https://instagram.com/${stylist.instagram_account}`} target="_blank">
                    {stylist.instagram_account}
                  </a>
                </InstagramAccount>
              )}
              <Buttons>
                <ReserveButton
                  route={routeFactories.reservation.getRoute(stylist.id)}
                  look="primary"
                  style={{
                    pointerEvents: reservedSpots.length > 0 ? 'auto' : 'none',
                    backgroundColor: reservedSpots.length > 0 ? '' : 'gray',
                  }}>
                  {reservedSpots.length > 0 ? catalogue.reserve : catalogue.noReserve}
                </ReserveButton>
                <ReserveButton look="white" bordered onClick={onFavoriteButtonClicked}>
                  {favorite ? catalogue.removeFavorite : catalogue.addFavorite}
                </ReserveButton>
                <ConciergeButton onClick={() => {window.location.href = "../concierge";}}>
                  Your Booking Concierge Service
                  <div>※Concierge Service only in English now.</div>
                </ConciergeButton>
              </Buttons>
              {!me && (
                <DescriptionNotMe>
                  <small>
                    {catalogue.membersRegistrationAttention}
                    <br/>
                    {catalogue.membersRegistrationGuidance}
                    <a onClick={onSignUpButtonClicked}>{catalogue.membersRegistrationLink}</a>
                  </small>
                </DescriptionNotMe>
              )}
            </StylistWrap>
            <InfoContainer>
              {menus.length != 0 && (
                <>
                  <MenuTitle>Menu</MenuTitle>
                  <div>{catalogue.selectableMenu}</div>
                  <MenuList>
                    {menus.map((menu, i) => (
                      <MenuItem key={i}>
                        <MenuContent>
                          <MenuName>
                            {menu.special_spot && `【${menu.special_spot.name}】`}
                            {language == 'ja' ? menu.menu_ja : menu.menu_en}
                          </MenuName>
                          <MenuDetail>
                            <MultiLineText text={language == 'ja' ? menu.detail_ja : menu.detail_en} />
                          </MenuDetail>
                        </MenuContent>
                        <MenuPrice>
                          <PriceText>¥{numberToCommaSeparatedString(menu.price)}</PriceText>({catalogue.excludingTax})
                        </MenuPrice>
                      </MenuItem>
                    ))}
                  </MenuList>
                </>
              )}
              <Items>
                {stylist.historyDisplay && (
                  <Item>
                    <Title>{catalogue.history}</Title>
                    <Text>
                      {stylist.history}
                      {catalogue.year}
                    </Text>
                  </Item>
                )}
                <Item>
                  <Title>{catalogue.specialtyImage}</Title>
                  <Text>{stylist.good_form}</Text>
                </Item>
                <Item>
                  <Title>{catalogue.specialtySkill}</Title>
                  <Text>{stylist.good_skill}</Text>
                </Item>
                <Item>
                  <Title>{catalogue.hobby}</Title>
                  <Text>{stylist.hobby}</Text>
                </Item>
                <Item>
                  <Title>{catalogue.message}</Title>
                  <Text>{stylist.comment}</Text>
                </Item>
              </Items>
              <IntroductionContainer>
                <Introduction>
                  <MultiLineText text={stylist.introduction} />
                </Introduction>
              </IntroductionContainer>
            </InfoContainer>
          </StylistContainer>
          {stylist.salons.length != 0 && (
            <>
              <AffiliationSalons>
                <AffiliationSalonsTitle>{catalogue.affiliation}</AffiliationSalonsTitle>
                <Salons>
                  {stylist.salons.map((salon, index) => {
                    return (
                      <>
                        <SalonWrapper>
                          <SalonInfo>
                            <Button
                              look="white"
                              style={{ fontWeight: 'bold' }}
                              route={routeFactories.salons.getRoute(salon.id)}>
                              <SalonNameText>{salon.name}</SalonNameText>
                            </Button>
                            {salon.name_en != '' && <SalonNameEn>{salon.name_en}</SalonNameEn>}
                            <SalonInfoItem>
                              <img src="/static/open.png" width={32} style={{ marginRight: '.5em' }} />
                              <SalonTimeText>
                                <MultiLineText text={salon.openingTime} />
                              </SalonTimeText>
                            </SalonInfoItem>
                            <SalonInfoItem>
                              <img src="/static/close.png" width={32} style={{ marginRight: '.5em' }} />
                              <SalonTimeText>
                                <MultiLineText text={salon.closingTime} />
                              </SalonTimeText>
                            </SalonInfoItem>
                            <SalonInfoItem>
                              <Payment size="25" style={{ marginRight: '.5em', marginLeft: '.2em' }} />
                              <MultiLineText text={salon.paymentMethod} />
                            </SalonInfoItem>
                            <SalonInfoItem>
                              <Place size="25" style={{ marginRight: '.5em', marginLeft: '.2em' }} />
                              <SalonAddressText>
                                {salon.postCode} {salon.address}
                              </SalonAddressText>
                            </SalonInfoItem>
                            <SalonInfoItem>
                              <Train size="25" style={{ marginRight: '.5em', marginLeft: '.2em' }} />
                              {salon.nearStation}
                            </SalonInfoItem>
                            {salon.area && salon.area != '' && (
                              <SalonInfoItem>
                                <MapPin size="25" style={{ marginRight: '.5em', marginLeft: '.2em' }} />
                                {salon.area}
                              </SalonInfoItem>
                            )}
                            <SalonInfoItem>
                              <Phone size="25" style={{ marginRight: '.5em', marginLeft: '.2em' }} />
                              {salon.phoneNumber}
                            </SalonInfoItem>
                            {salon.url && salon.url != '' && (
                              <SalonInfoItem>
                                <Web size="25" style={{ marginRight: '.5em', marginLeft: '.2em' }} />
                                <SalonUrl href={salon.url} target="blank">
                                  {salon.url}
                                </SalonUrl>
                              </SalonInfoItem>
                            )}
                          </SalonInfo>
                        </SalonWrapper>
                        {geos.length != 0 && (
                          <MapContainer>
                            <GoogleMap
                              key={'map-' + index}
                              center={{ lat: geos[index].lat, lng: geos[index].lng }}
                              text={''}
                              buttonText={catalogue.showMapMobile}
                              language={getPreferredLanguage()}
                            />
                          </MapContainer>
                        )}
                      </>
                    )
                  })}
                </Salons>
              </AffiliationSalons>
            </>
          )}
        </Container>
      </Section>
    </>
  )
}

Stylists.getInitialProps = async ({ req, me, query }: KamismaPageContext) => {
  const catalogue = await loadCatalogue<I18nCatalogue>('stylists', me, req)
  if (!query.id) {
    return { catalogue, statusCode: 404 }
  }

  const stylist = await fetchHairStylist(query.id.toString(), req)
  if (stylist === null) {
    return { catalogue, statusCode: 404 }
  }

  let menus: Menu[] = []
  try {
    menus = await fetchMenus(stylist.id.toString())
  } catch (error) {
    return { catalogue, statusCode: 404 }
  }

  let reservedSpots: ReservedSpots = []
  const now_date = new Date()
  const now_day = new Date(now_date.getFullYear(), now_date.getMonth(), now_date.getDate(), 0, 0, 0)
  for (let salon of stylist!.salons) {
    let spots = await fetchReservedSpots(stylist!.id.toString(), salon.id.toString(), false)
    if (spots != null) {
      spots.forEach((spot: any) => {
        if (new Date(spot.dateTime) > now_day && spot.spots > 0) {
          reservedSpots.push(spot)
        }
      })
    }
  }

  let geos: GeoLocation[] = []
  if (stylist.salons.length != 0) {
    for (const salon of stylist.salons) {
      const addressWithPostCode = '〒' + salon.postCode + ' ' + salon.address
      const geo = await getGeoLocationFromAddress(addressWithPostCode)
      if (geo != null) {
        geos.push(geo)
      }
    }
  }

  return { catalogue, stylist, menus, reservedSpots, geos }
}

export default Stylists

const StylistContainer = styled.div`
  display: flex;
  margin-top: 36px;

  @media ${devices.mobile} {
    display: block;
    margin-top: 20px;
  }
`
const StylistWrap = styled.div`
  width: 280px;

  @media ${devices.mobile} {
    margin: auto;
  }
`
const NameEn = styled.span`
  color: gray;
  font-size: 16px;
  margin-left: 18px;

  @media ${devices.mobile} {
    display: block;
    margin-left: 0px;
  }
`

const SalonNameEn = styled.span`
  color: gray;
  font-size: 18px;
  display: block;
  margin-bottom: 24px;
  margin-left: 14px;
`

const Thumb = styled.div`
  width: 280px;
  border: 1px solid ${colors.secondaryDark};
  line-height: 0;

  @media ${devices.mobile} {
    width: 100%;
  }
`
const InstagramAccount = styled.div`
  margin-top: 15px;
  line-height: 25px;

  a {
    display: inline-block;
    margin-left: 5px;
    text-decoration: underline;
    color: ${colors.secondaryDark};

    &:hover {
      text-decoration: none;
    }
  }
`
const InfoContainer = styled.div`
  flex: 1;
  margin-left: 50px;
  font-size: 16px;
  line-height: 30px;

  @media ${devices.mobile} {
    margin-left: 0;
    margin-top: 20px;
  }
`
const MenuTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: normal;
  border-block-end: solid 1px gray;
  padding-bottom: 12px;
`
const MenuList = styled.ul`
  list-style: none;
  margin: 20px 0 0;
  padding: 0;
  border: 1px solid #ccc;
`
const MenuItem = styled.li`
  display: block;
  justify-content: space-between;
  padding: 15px;
  background-color: ${colors.gray50};

  & + & {
    border-top: 1px solid #ccc;
  }
`
const MenuContent = styled.div`
  width: 100%;
`
const MenuName = styled.div`
  font-weight: bold;
`
const MenuDetail = styled.div`
  font-size: 14px;
  line-height: 20px;
`
const MenuPrice = styled.div`
  padding-left: 10px;
  text-align: right;
  @media ${devices.mobile} {
    font-size: 14px;
  }
`
const PriceText = styled.span`
  font-size: 24px;
  padding-right: 3px;
  @media ${devices.mobile} {
    font-size: 20px;
  }
`

const AffiliationSalons = styled.div`
  padding-top: 20px;
`

const AffiliationSalonsTitle = MenuTitle

const Salons = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  overflow: auto;
`

const SalonWrapper = styled.div`
  width: 100%;
  padding: 12px 0px;
  display: flex;
  @media (max-width: 800px) {
    display: block;
  }
`

const SalonInfo = styled.div`
  width: 100%;
  @media ${devices.mobile} {
    margin-bottom: 20px;
  }
`

const SalonInfoItem = styled.div`
  margin-left: 0.5rem;
  margin-bottom: 0.3rem;
`

const SalonNameText = styled.span`
  font-size: 34px;
  @media ${devices.mobile} {
    font-size: 23px;
  }
`

const SalonTimeText = styled.div`
  position: relative;
  top: -4px;
  display: inline-flex;
`

const SalonAddressText = styled.div`
  width: 88%;
  display: inline-flex;
`

const SalonUrl = styled.a`
  color: #5a5129;
  text-decoration: none;
`

const MapContainer = styled.div`
  display: block;
  width: 100%;
  padding-bottom: 30px;
  border-block-end: solid 1px gray;
`

const Introduction = styled.div`
  flex: 1;
  margin-bottom: 36px;
  font-size: 16px;
  line-height: 30px;
`

const IntroductionContainer = styled.div`
  padding: 20px;
  background-color: ${colors.secondaryExtraLight};
  margin-bottom: 36px;
`

const Items = styled.div`
  margin: 36px 0px;
  padding: 20px;
  border: 1px solid #ccc;
`
const Item = styled.div`
  display: flex;
  font-size: 16px;
  line-height: 30px;

  & + & {
    margin-top: 10px;
  }

  @media ${devices.mobile} {
    flex-direction: column;
  }
`
const Title = styled.div`
  font-weight: bold;
`
const Text = styled.div`
  flex: 1;

  @media ${devices.mobile} {
    padding-left: 1em;
  }

  &::before {
    content: '／';
    display: inline-block;
    margin-right: 0.2em;
    margin-left: 0.2em;

    @media ${devices.mobile} {
      content: none;
    }
  }
`
const Buttons = styled.div`
  margin-top: 12px;
  text-align: center;
`
const ReserveButton = styled(Button)`
  flex-direction: column;
  width: 100%;
  margin: 6px 0px;
`

const ConciergeButton = styled.div`
  width: 100%;
  margin: 6px 0px;
  padding: 5px;
  border-radius: 3px;
  background-color: rgba(214, 80, 79, 0.8);
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.0rem;
  cursor: pointer

  div:last-child {
    font-size: 0.7rem;
  }
`

const GallerySlide = styled.div`
  width: 100%;
  height: 600px;
  display: grid;

  @media ${devices.mobile} {
    height: 200px;
  }
`

const ProfileImg = styled.img`
  display: block;
  margin: auto;
  margin-top: 24px;
`

const DescriptionNotMe = styled.div`
  a {
    cursor: pointer;
    display: inline-block;
    text-decoration: none;
    color: ${colors.brown};

    &:hover {
      text-decoration: underline;
    }
  }

  @media ${devices.mobile} {
    text-align: center;
  }
`
